import { z } from 'zod';

/**
 * Wire contract of offerings-service's `/odoo/*` admin API.
 *
 * Odoo ids are positive integers on the wire. Everything optional on the
 * server is `nullish` here so a backend that omits a field degrades to "not
 * set" instead of failing the whole page's parse.
 */

export const PAYMENT_METHOD_KEYS = [
  'evc_plus',
  'zaad',
  'sahal',
  'mpesa',
  'cash',
] as const;
export const PaymentMethodKeySchema = z.enum(PAYMENT_METHOD_KEYS);
export type PaymentMethodKey = z.infer<typeof PaymentMethodKeySchema>;

export const OdooDisabledReasonSchema = z.enum([
  'settings_invalid',
  'settings_disabled',
  'platform_unavailable',
  'no_credential',
  'credential_incomplete',
  'credential_invalid_url',
]);
export type OdooDisabledReason = z.infer<typeof OdooDisabledReasonSchema>;

export const ReadinessSectionSchema = z.enum([
  'connection',
  'accounting',
  'stock',
]);
export type ReadinessSection = z.infer<typeof ReadinessSectionSchema>;

export const READINESS_SECTIONS = ReadinessSectionSchema.options;

export const ReadinessCheckSchema = z.object({
  key: z.string(),
  label: z.string(),
  ok: z.boolean(),
  detail: z.string().nullish(),
  section: ReadinessSectionSchema,
  severity: z.enum(['blocking', 'warning']).default('blocking'),
});
export type ReadinessCheck = z.infer<typeof ReadinessCheckSchema>;

export const OutboxCountsSchema = z.object({
  pending: z.number().default(0),
  sent: z.number().default(0),
  abandoned: z.number().default(0),
});
export type OutboxCounts = z.infer<typeof OutboxCountsSchema>;

export const OdooStatusSchema = z.object({
  credentialConfigured: z.boolean().nullable(),
  enabled: z.boolean(),
  shadowMode: z.boolean(),
  goLiveAt: z.string().nullish(),
  accountingActive: z.boolean(),
  disabledReason: OdooDisabledReasonSchema.nullish(),
  odooUrl: z.string().nullish(),
  accountingReady: z.boolean(),
  stockReady: z.boolean(),
  readiness: z.array(ReadinessCheckSchema).default([]),
  counts: OutboxCountsSchema.default({}),
  oldestPendingAt: z.string().nullish(),
  oldestPendingAgeSeconds: z.number().nullish(),
});
export type OdooStatus = z.infer<typeof OdooStatusSchema>;

const OdooIdSchema = z.number().int().positive();

/** `split` posts "Device sale" at cash price plus a "Murabaha markup" line; totals are the same. */
export const InvoiceShapeSchema = z.enum(['single', 'split']);
export type InvoiceShape = z.infer<typeof InvoiceShapeSchema>;

/** `journal_entry`: MazeedPay posts COGS at activation and reverses it on return. */
export const CogsModeSchema = z.enum(['none', 'journal_entry']);
export type CogsMode = z.infer<typeof CogsModeSchema>;

export const OdooSettingsSchema = z.object({
  enabled: z.boolean(),
  shadowMode: z.boolean(),
  goLiveAt: z.string().nullish(),
  methodJournalIds: z.record(z.string(), OdooIdSchema.nullable()).default({}),
  incomeAccountId: OdooIdSchema.nullish(),
  salesJournalId: OdooIdSchema.nullish(),
  branchAnalyticIds: z.record(z.string(), OdooIdSchema).default({}),
  costOfGoodsAccountId: OdooIdSchema.nullish(),
  stockValuationAccountId: OdooIdSchema.nullish(),
  invoiceShape: InvoiceShapeSchema.default('single'),
  markupIncomeAccountId: OdooIdSchema.nullish(),
  cogsMode: CogsModeSchema.default('none'),
  miscJournalId: OdooIdSchema.nullish(),
  badDebtAccountId: OdooIdSchema.nullish(),
  /** Unset means recoveries credit the bad debt account (server default). */
  badDebtRecoveryAccountId: OdooIdSchema.nullish(),
  /** Credit side of the opening invoices posted for contracts activated before go-live. */
  openingBalanceAccountId: OdooIdSchema.nullish(),
  vendorLocationId: OdooIdSchema.nullish(),
  customerLocationId: OdooIdSchema.nullish(),
  parentLocationId: OdooIdSchema.nullish(),
  receiptPickingTypeId: OdooIdSchema.nullish(),
  internalPickingTypeId: OdooIdSchema.nullish(),
  deliveryPickingTypeId: OdooIdSchema.nullish(),
  returnPickingTypeId: OdooIdSchema.nullish(),
});
export type OdooSettings = z.infer<typeof OdooSettingsSchema>;

/** The whole object replaces the stored one; `confirmRewind` is never stored. */
export const OdooSettingsUpdateSchema = OdooSettingsSchema.extend({
  methodJournalIds: z.record(z.string(), OdooIdSchema.nullable()),
  branchAnalyticIds: z.record(z.string(), OdooIdSchema),
  confirmRewind: z.boolean().optional(),
});
export type OdooSettingsUpdate = z.infer<typeof OdooSettingsUpdateSchema>;

export const ConnectionErrorKindSchema = z.enum([
  'auth',
  'unreachable',
  'database',
  'permission',
  'other',
  'no_credential',
  'credential_incomplete',
  'credential_invalid_url',
  'platform_unavailable',
]);
export type ConnectionErrorKind = z.infer<typeof ConnectionErrorKindSchema>;

export const ConnectionTestResultSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    companyName: z.string().nullish(),
    currency: z.string().nullish(),
    latencyMs: z.number().nullish(),
  }),
  z.object({
    ok: z.literal(false),
    errorKind: ConnectionErrorKindSchema.catch('other'),
    message: z.string().nullish(),
  }),
]);
export type ConnectionTestResult = z.infer<typeof ConnectionTestResultSchema>;

export const SetupProblemSchema = z.enum([
  'not_configured',
  'not_found',
  'archived',
  'wrong_type',
  'wrong_currency',
  'unavailable',
  'check_failed',
  'connection_failed',
]);
export type SetupProblem = z.infer<typeof SetupProblemSchema>;

export const SetupCheckItemSchema = z.object({
  key: z.string(),
  kind: z.string(),
  section: ReadinessSectionSchema,
  severity: z.enum(['blocking', 'warning']).catch('blocking'),
  ok: z.boolean(),
  problem: SetupProblemSchema.nullish().catch(null),
  label: z.string(),
  detail: z.string().nullish(),
  fix: z.string().nullish(),
  subject: z.string().nullish(),
  odooId: z.number().nullish(),
  odooName: z.string().nullish(),
  actual: z.string().nullish(),
  expected: z.array(z.string()).default([]),
  /** Readiness key this item verifies; absent or unknown lands under "Other Odoo checks". */
  requirement: z.string().nullish(),
});
export type SetupCheckItem = z.infer<typeof SetupCheckItemSchema>;

export const SetupCheckResultSchema = z.object({
  ok: z.boolean(),
  checkedAt: z.string(),
  errorKind: ConnectionErrorKindSchema.nullish().catch('other'),
  message: z.string().nullish(),
  companyName: z.string().nullish(),
  currency: z.string().nullish(),
  items: z.array(SetupCheckItemSchema).default([]),
});
export type SetupCheckResult = z.infer<typeof SetupCheckResultSchema>;

/** Platform-service credential status row; the secret is never returned. */
export const PlatformCredentialSchema = z.object({
  id: z.string(),
  providerType: z.string(),
  providerName: z.string(),
  isActive: z.boolean(),
  stage: z.string(),
  createdAt: z.string(),
});
export type PlatformCredential = z.infer<typeof PlatformCredentialSchema>;

export const ODOO_PROVIDER = {
  providerType: 'erp',
  providerName: 'odoo',
} as const;

/** Write-only: what platform-service encrypts as the tenant's Odoo credential. */
export const OdooCredentialSchema = z.object({
  url: z.string(),
  database: z.string(),
  apiKey: z.string(),
});
export type OdooCredential = z.infer<typeof OdooCredentialSchema>;

export const LookupKindSchema = z.enum([
  'journals',
  'analytic-accounts',
  'income-accounts',
  'expense-accounts',
  'opening-accounts',
  'locations',
  'picking-types',
]);
export type LookupKind = z.infer<typeof LookupKindSchema>;

export const LocationUsageSchema = z.enum([
  'internal',
  'supplier',
  'customer',
  'view',
  'inventory',
  'production',
  'transit',
]);
export type LocationUsage = z.infer<typeof LocationUsageSchema>;

export const LookupItemSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  code: z.string().nullish(),
  type: z.string().nullish(),
});
export type LookupItem = z.infer<typeof LookupItemSchema>;

export const EventStatusSchema = z.enum(['pending', 'sent', 'abandoned']);
export type EventStatus = z.infer<typeof EventStatusSchema>;

export const SubjectTypeSchema = z.enum([
  'customer',
  'contract',
  'payment',
  'device_unit',
  'device_variant',
  'branch',
]);
export type SubjectType = z.infer<typeof SubjectTypeSchema>;

export const ODOO_EVENT_TYPES = [
  'contract.activated',
  'payment.completed',
  'payment.reversed',
  'contract.cancelled',
  'contract.written_off',
  'contract.opening_balance',
  'customer.updated',
  'unit.received',
  'unit.transferred',
  'unit.delivered',
  'unit.returned',
  'unit.scrapped',
  'variant.updated',
  'branch.created',
  'branch.updated',
] as const;
export const OdooEventTypeSchema = z.enum(ODOO_EVENT_TYPES);
export type OdooEventType = z.infer<typeof OdooEventTypeSchema>;

export const OdooEventSchema = z.object({
  id: z.string(),
  eventType: z.string(),
  subjectType: SubjectTypeSchema,
  subjectId: z.string(),
  status: EventStatusSchema,
  odooId: z.number().nullish(),
  odooModel: z.string().nullish(),
  lastError: z.string().nullish(),
  attemptCount: z.number().default(0),
  nextRetryAt: z.string().nullish(),
  createdAt: z.string(),
  sentAt: z.string().nullish(),
  abandonedAt: z.string().nullish(),
  payloadSummary: z.record(z.string(), z.unknown()).default({}),
  payload: z.record(z.string(), z.unknown()).nullish(),
});
export type OdooEvent = z.infer<typeof OdooEventSchema>;

export const OdooEventPageSchema = z.object({
  items: z.array(OdooEventSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});
export type OdooEventPage = z.infer<typeof OdooEventPageSchema>;

export const OdooEventStatsSchema = z.object({
  byStatus: OutboxCountsSchema,
  byEventType: z.array(OutboxCountsSchema.extend({ eventType: z.string() })),
});
export type OdooEventStats = z.infer<typeof OdooEventStatsSchema>;

export const RetryAbandonedResultSchema = z.object({
  requeued: z.number(),
  remaining: z.number(),
});
export type RetryAbandonedResult = z.infer<typeof RetryAbandonedResultSchema>;

export const OdooEventFiltersSchema = z.object({
  status: EventStatusSchema.optional(),
  eventType: z.string().optional(),
  subjectType: SubjectTypeSchema.optional(),
  subjectId: z.string().optional(),
  contractId: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  search: z.string().optional(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(200).optional(),
  includePayload: z.boolean().optional(),
});
export type OdooEventFilters = z.infer<typeof OdooEventFiltersSchema>;

export const SYNC_STATE_MAX_ITEMS = 200;

export const SyncStatusSchema = z.enum([
  'synced',
  'pending',
  'failed',
  'not_tracked',
]);
export type SyncStatus = z.infer<typeof SyncStatusSchema>;

export const SyncStateItemSchema = z.object({
  subjectType: SubjectTypeSchema,
  subjectId: z.string(),
});
export type SyncStateItem = z.infer<typeof SyncStateItemSchema>;

export const SyncStateEntrySchema = z.object({
  status: SyncStatusSchema,
  odooId: z.number().nullish(),
  odooModel: z.string().nullish(),
  lastError: z.string().nullish(),
  eventId: z.string().nullish(),
});
export type SyncStateEntry = z.infer<typeof SyncStateEntrySchema>;

export const SyncStateResponseSchema = z.object({
  odooUrl: z.string().nullish(),
  items: z.record(z.string(), SyncStateEntrySchema).default({}),
});
export type SyncStateResponse = z.infer<typeof SyncStateResponseSchema>;

export const ODOO_ERROR_CODES = {
  noCredential: 'odoo_no_credential',
  credentialIncomplete: 'odoo_credential_incomplete',
  rewindRequiresConfirmation: 'go_live_rewind_requires_confirmation',
  platformUnavailable: 'platform_unavailable',
} as const;

export const ODOO_RECONCILIATION_ERROR_CODES = {
  running: 'reconciliation_running',
  notConfigured: 'odoo_not_configured',
} as const;

export const ReconciliationStatusSchema = z.enum([
  'matched',
  'drift',
  'sync_pending',
  'sync_failed',
  'missing_invoice',
  'not_tracked',
  'odoo_unavailable',
]);
export type ReconciliationStatus = z.infer<typeof ReconciliationStatusSchema>;
export const RECONCILIATION_STATUSES = ReconciliationStatusSchema.options;

const MoneySchema = z.number().nullish().transform((v) => v ?? null);

export const ReconciliationRowSchema = z.object({
  contractId: z.string(),
  contractNumber: z.string().nullish(),
  customerName: z.string().nullish(),
  branchId: z.string().nullish(),
  branchName: z.string().nullish(),
  contractStatus: z.string().nullish(),
  writtenOff: z.boolean().default(false),
  loanOutstanding: MoneySchema,
  odooResidual: MoneySchema,
  expectedResidual: MoneySchema,
  difference: MoneySchema,
  status: ReconciliationStatusSchema,
  odooInvoiceId: z.number().int().positive().nullish(),
  checkedAt: z.string().nullish(),
  detail: z.string().nullish(),
});
export type ReconciliationRow = z.infer<typeof ReconciliationRowSchema>;

export const ReconciliationPageSchema = z.object({
  items: z.array(ReconciliationRowSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});
export type ReconciliationPage = z.infer<typeof ReconciliationPageSchema>;

export const ReconciliationCountsSchema = z.object({
  matched: z.number().default(0),
  drift: z.number().default(0),
  sync_pending: z.number().default(0),
  sync_failed: z.number().default(0),
  missing_invoice: z.number().default(0),
  not_tracked: z.number().default(0),
  odoo_unavailable: z.number().default(0),
});
export type ReconciliationCounts = z.infer<typeof ReconciliationCountsSchema>;

export const ReconciliationSummarySchema = z.object({
  counts: ReconciliationCountsSchema.default({}),
  driftTotal: z.number().default(0),
  contractsChecked: z.number().default(0),
  lastRunAt: z.string().nullish(),
  running: z.boolean().default(false),
  lastError: z.string().nullish(),
});
export type ReconciliationSummary = z.infer<typeof ReconciliationSummarySchema>;

export const ReconciliationRunResultSchema = z.object({
  queued: z.boolean(),
});
export type ReconciliationRunResult = z.infer<
  typeof ReconciliationRunResultSchema
>;

export const ReconciliationFiltersSchema = z.object({
  status: ReconciliationStatusSchema.optional(),
  branchId: z.string().optional(),
  search: z.string().optional(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(200).optional(),
});
export type ReconciliationFilters = z.infer<typeof ReconciliationFiltersSchema>;

export const ODOO_OPENING_BALANCE_ERROR_CODES = {
  goLiveNotSet: 'go_live_not_set',
  notConfigured: 'odoo_not_configured',
  loanServiceUnavailable: 'loan_service_unavailable',
  countMismatch: 'count_mismatch',
  accountNotSet: 'opening_account_not_set',
} as const;

/** Money as a number; a Decimal serialized as a numeric string is accepted too. */
const AmountSchema = z
  .union([z.number(), z.string().regex(/^-?\d+(\.\d+)?$/)])
  .transform(Number);

export const OpeningBalanceExclusionSchema = z.enum([
  'zero_balance',
  'written_off',
  'already_in_odoo',
  'no_loan',
  'loan_unknown',
  'not_eligible_status',
]);
export type OpeningBalanceExclusion = z.infer<typeof OpeningBalanceExclusionSchema>;
export const OPENING_BALANCE_EXCLUSIONS = OpeningBalanceExclusionSchema.options;

export const OpeningBalanceExcludedSchema = z.object({
  zero_balance: z.number().default(0),
  written_off: z.number().default(0),
  already_in_odoo: z.number().default(0),
  no_loan: z.number().default(0),
  loan_unknown: z.number().default(0),
  not_eligible_status: z.number().default(0),
});
export type OpeningBalanceExcluded = z.infer<typeof OpeningBalanceExcludedSchema>;

export const OpeningBalanceItemSchema = z.object({
  contractId: z.string(),
  contractNumber: z.string().nullish(),
  customerName: z.string().nullish(),
  branchName: z.string().nullish(),
  loanOutstanding: AmountSchema,
  paymentsSinceGoLive: AmountSchema,
  openingAmount: AmountSchema,
  dueDate: z.string().nullish(),
});
export type OpeningBalanceItem = z.infer<typeof OpeningBalanceItemSchema>;

export const OpeningBalancePreviewSchema = z.object({
  asOf: z.string(),
  openingDate: z.string(),
  totals: z.object({ contracts: z.number().int().min(0), amount: AmountSchema }),
  excluded: OpeningBalanceExcludedSchema.default({}),
  items: z.array(OpeningBalanceItemSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});
export type OpeningBalancePreview = z.infer<typeof OpeningBalancePreviewSchema>;

export const OpeningBalanceFiltersSchema = z.object({
  search: z.string().optional(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(200).optional(),
});
export type OpeningBalanceFilters = z.infer<typeof OpeningBalanceFiltersSchema>;

export const OpeningBalancePostRequestSchema = z.object({
  confirmCount: z.number().int().min(1),
});
export type OpeningBalancePostRequest = z.infer<typeof OpeningBalancePostRequestSchema>;

export const OpeningBalancePostResultSchema = z.object({
  enqueued: z.number().int().min(0),
  skipped: z.number().int().min(0).default(0),
  totalAmount: AmountSchema,
});
export type OpeningBalancePostResult = z.infer<typeof OpeningBalancePostResultSchema>;
